import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {EmTranslatePipe, TranslationService} from '@emma-labs/shared/angular';
import {
  ENVIRONMENT,
  EnvironmentBroker,
  ModificationNoticeBreakdown,
  SupportedLanguageCode,
  SURCHARGE_OPTIONS,
  TextTranslations,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConfirmationService} from 'primeng/api';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {DialogService} from 'primeng/dynamicdialog';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {SplitButtonModule} from 'primeng/splitbutton';
import {filter, first} from 'rxjs';
import {SurchargeSummary} from '../../../entities/underwriting-page-object/surcharge-summary';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {ProfileApprovalPopupComponent} from '../profile-approval-popup/profile-approval-popup.component';
import {ProfileRefusalPopupComponent} from '../profile-refusal-popup/profile-refusal-popup.component';
import {ProtectionSummaryComponent} from '../protection-summary.component';

@UntilDestroy()
@Component({
  selector: 'em-modification-section',
  templateUrl: './modification-section.component.html',
  imports: [
    CardModule,
    ProtectionSummaryComponent,
    DropdownModule,
    ReactiveFormsModule,
    ButtonModule,
    ProfileRefusalPopupComponent,
    SplitButtonModule,
    ProgressSpinnerModule,
    EmTranslatePipe,
  ],
})
export class ModificationSectionComponent extends BaseSectionComponent<
  FormControl<number>,
  SurchargeSummary,
  number
> {
  protected readonly surchargeOptions = SURCHARGE_OPTIONS;

  isProfileRefusalPopupDisplayed = false;
  refusalPopupInfo!: {clientFullName: string; policyNumber: string; languageCode: SupportedLanguageCode};
  _surchargeSummary!: SurchargeSummary;
  isApproveLoading = false;
  isConditionalApproveLoading = false;
  isUpdatingSurcharge = false;

  @Input({required: true})
  override set props(surchargeSummary: SurchargeSummary) {
    this._surchargeSummary = surchargeSummary;
    this.refusalPopupInfo = {
      clientFullName: surchargeSummary.clientFullName,
      policyNumber: surchargeSummary.policyNumber,
      languageCode: surchargeSummary.languageCode,
    };

    if (surchargeSummary.surchargedProtection) {
      this.form.setValue(surchargeSummary.surchargedProtection.surchargeRate, {emitEvent: false});
    } else {
      this.form.reset(0, {emitEvent: false});
    }

    this.isUpdatingSurcharge = false;
  }

  @Output() profileRefused = new EventEmitter<{
    refusalMessage: TextTranslations;
    callback: (status: boolean) => void;
  }>();

  @Output() profileApproved = new EventEmitter<{
    callback?: (status: boolean) => void;
    modificationNoticeBreakdown?: ModificationNoticeBreakdown;
  }>();

  constructor(
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
    private readonly fb: NonNullableFormBuilder,
    private readonly confirmationService: ConfirmationService,
    private readonly translate: TranslationService,
    private readonly dialogService: DialogService,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  openConfirmationPopup(): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      header: this.translate.currentLanguageIsFr() ? 'Approuver la proposition' : 'Approve the proposal',
      message: this.translate.currentLanguageIsFr()
        ? "Vous êtes sur le point d'approuver la proposition. Veuillez confirmer en cliquant sur le bouton confirmer."
        : 'You are about to approve the proposal. Please confirm by clicking on the confirm button.',
      acceptLabel: this.translate.currentLanguageIsFr() ? 'Confirmer' : 'Confirm',
      rejectLabel: this.translate.currentLanguageIsFr() ? 'Annuler' : 'Cancel',
      rejectButtonStyleClass: 'reject-btn',
      acceptIcon: 'pi',
      rejectIcon: 'pi',
      accept: () => {
        this.isApproveLoading = true;
        this.profileApproved.emit({
          callback: (_) => this.resetButtonLoading(),
        });
      },
    });
  }

  openProfileRefusalPopup() {
    this.isProfileRefusalPopupDisplayed = true;
  }

  openProfileApprovalPopup(withCondition: boolean) {
    if (this._surchargeSummary.surchargedProtectionSummary || withCondition) {
      const ref = this.dialogService.open(ProfileApprovalPopupComponent, {
        width: '95%',
        data: {surchargeSummary: this._surchargeSummary, withCondition},
      });

      ref.onClose
        .pipe(
          first(),
          filter((result) => !!result),
        )
        .subscribe((result) => {
          if (withCondition) {
            this.isConditionalApproveLoading = true;
          } else {
            this.isApproveLoading = true;
          }

          this.profileApproved.emit({
            modificationNoticeBreakdown: result,
            callback: () => this.resetButtonLoading(),
          });
        });
    } else {
      this.openConfirmationPopup();
    }
  }

  onProfileRefused(event: {refusalMessage: TextTranslations; callback: () => void}): void {
    this.profileRefused.emit({
      refusalMessage: event.refusalMessage,
      callback: (status) => {
        this.isProfileRefusalPopupDisplayed = !status;
        event.callback();
      },
    });
  }

  private createForm(): FormControl<number> {
    const form = this.fb.control<number>(0);

    form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.isUpdatingSurcharge = true;

      this.propsChange.emit({output: value});
    });

    return form;
  }

  private resetButtonLoading(): void {
    this.isApproveLoading = false;
    this.isConditionalApproveLoading = false;
  }
}
